import globals from "@/util/global-whitelist.mjs";

const BASE_64_CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

/**
 * Encodes a string into Base64 format, prioritizing `btoa`, then falling back to `Buffer`,
 * and finally using a manual encoding method if neither is available.
 *
 * @param {string} input - The input string to encode.
 * @returns {string} - The Base64-encoded string.
 */
export function encodeBase64(input: string): string {
  try {
    // Use btoa if available
    if (typeof btoa === "function") return btoa(input);
  } catch (_) {
    // Ignore and try the next approach
  }

  try {
    // Use Buffer if available (Node.js)
    if (typeof Buffer !== "undefined")
      return Buffer.from(input, "utf-8").toString("base64");
  } catch (_) {
    // Ignore and try the next approach
  }

  // Manual base64 encoding
  const encoder = new TextEncoder();
  const bytes = encoder.encode(input);

  let output = "";
  let padding = "";

  for (let i = 0; i < bytes.length; i += 3) {
    const chunk =
      (bytes[i] << 16) | (bytes[i + 1] << 8 || 0) | (bytes[i + 2] || 0);

    output += BASE_64_CHARS[(chunk >> 18) & 0x3f];
    output += BASE_64_CHARS[(chunk >> 12) & 0x3f];
    output +=
      i + 1 < bytes.length
        ? BASE_64_CHARS[(chunk >> 6) & 0x3f]
        : (padding = "=");
    output +=
      i + 2 < bytes.length ? BASE_64_CHARS[chunk & 0x3f] : (padding += "=");
  }

  return output;
}

const getSHA256Hash = async (input: string): Promise<string> => {
  const textAsBuffer = new TextEncoder().encode(input);
  const hashBuffer = await globals.crypto.subtle.digest(
    "SHA-256",
    textAsBuffer,
  );
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray
    .map((item) => item.toString(16).padStart(2, "0"))
    .join("");
  return hash;
};

export default getSHA256Hash;
